/**
 * Radio Auto-Refill — persistent module-level subscription
 *
 * Survives React component unmounts (navigating away from MaestroRadioPage).
 * Watches the maestroStore queue index and triggers continuation when the
 * queue runs low, keeping radio playback infinite until explicitly stopped.
 *
 * Polls ambient weather/time every 5 minutes during active sessions and re-weaves
 * the fluid queue zone when conditions shift (committed current + next 2 untouched).
 */

import { useMaestroStore } from '../store/maestroStore';
import { smartPlaylistService } from '../services/smartPlaylistService';
import { QueueController } from '../transport/QueueController';
import api from '../services/api';
import { isSessionDead } from '../lib/sessionDead';
import { ellaTracksToQueueFormat } from './ellaQueueFormat';
import {
  ellaRadioSessionRefs,
  resetEllaRadioSessionRefs,
  syncEllaRadioSessionRefs,
  type EllaRadioSessionRefs,
} from './ellaRadioSessionRefs';
import {
  applyAmbientSlidersToTuning,
  fetchFreshEllaContinueOpts,
} from './ellaRadioAmbient';
import {
  resetAmbientReweaveState,
  seedAmbientReweaveSignature,
  setLocalPoolSlice,
  startAmbientPoll,
  stopAmbientPoll,
} from './radioAmbientReweave';
import {
  bankReserveTracks,
  mergeIntoReserve,
  pullFromReserve,
  resetReserveTank,
  reserveTankSize,
} from './radioReserve';
import {
  resetRadioTrackRegistry,
  seedRadioTrackRegistry,
  syncRadioTrackRegistryFromQueue,
} from './radioTrackRegistry';
import { isEllaRadioBuildPollActive } from './ellaRadioBuildPoll';
import { isRadioArcIngestionPending } from './ellaRadioSessionState';

// ── Types ────────────────────────────────────────────────────────────

export type RegisterRadioSessionInput = Pick<EllaRadioSessionRefs, 'ellaPlaylistId'> &
  Partial<Omit<EllaRadioSessionRefs, 'ellaPlaylistId'>>;

export interface RegisterRadioSessionOptions {
  /** Stage-2 build poll owns ingestion until ready — defer refill watcher until armed. */
  deferRefillUntilBuildComplete?: boolean;
}

export interface RefillRequestOptions {
  /** Wait for refill when queue has run out (end-of-playlist). */
  urgent?: boolean;
  /** Weather/time mood changed — fetch next batch with fresh ambient. */
  moodShift?: boolean;
  /** Live DJ moment — insert tracks as play-next within one song. */
  momentInterrupt?: boolean;
  momentReason?: 'weather_shift' | 'time_shift' | 'combined_shift';
  previousAmbientLabel?: string;
  newAmbientLabel?: string;
  freshAmbient?: Record<string, unknown>;
}

// ── Module state ─────────────────────────────────────────────────────

let _active = false;
let _refilling = false;
let _refillWatcherArmed = false;
let _pendingMoment = false;
let _unsub: (() => void) | null = null;
let _lastSnapshot = '';

/** Tracks appended on the most recent refill (for QueueController end-of-queue wait). */
let _lastRefillAppended = 0;

const REFILL_THRESHOLD = 3;
const URGENT_REFILL_TIMEOUT_MS = 45_000;
const MOOD_SHIFT_DEBOUNCE_MS = 60_000;
const REFILL_RETRY_BACKOFF_MS = [2_000, 8_000, 20_000];
const REFILL_MAX_RETRIES = 3;
let _lastMoodShiftRefillAt = 0;
let _backgroundRetryInFlight = false;

// ── Public API ───────────────────────────────────────────────────────

export function registerRadioSession(
  ctx: RegisterRadioSessionInput,
  options: RegisterRadioSessionOptions = {},
): void {
  clearRadioSession();
  syncEllaRadioSessionRefs(ctx);
  _active = true;
  _refillWatcherArmed = !options.deferRefillUntilBuildComplete;
  syncRadioTrackRegistryFromQueue();
  resetReserveTank();
  resetAmbientReweaveState();
  if (_refillWatcherArmed) {
    _armRefillWatcher();
  } else {
    console.log('[radioRefill] session registered — refill deferred until build ready:', ctx.ellaPlaylistId);
  }
  void _seedAmbientSignature();
  startAmbientPoll();
}

/** Arm the refill watcher after stage-2 build poll reports ready (or partial). */
export function armRadioRefillWatcher(): void {
  if (!_active) return;
  if (_refillWatcherArmed) return;
  _refillWatcherArmed = true;
  syncRadioTrackRegistryFromQueue();
  _armRefillWatcher();
  console.log('[radioRefill] refill watcher armed after build completion');
}

export function clearRadioSession(): void {
  _active = false;
  _refilling = false;
  _refillWatcherArmed = false;
  _pendingMoment = false;
  _lastSnapshot = '';
  _lastRefillAppended = 0;
  resetRadioTrackRegistry();
  resetReserveTank();
  resetAmbientReweaveState();
  stopAmbientPoll();
  resetEllaRadioSessionRefs();
  void import('./ellaRadioSessionState').then(({ resetRadioSessionState }) => {
    resetRadioSessionState();
  });
  if (_unsub) {
    _unsub();
    _unsub = null;
  }
}

export function isRadioRefillActive(): boolean {
  return _active && ellaRadioSessionRefs.ellaPlaylistId != null;
}

/**
 * Request a refill — used by radioRefill watcher and QueueController at end-of-queue.
 * Returns true if new tracks were appended or inserted.
 */
export async function requestEllaRadioRefill(options: RefillRequestOptions = {}): Promise<boolean> {
  if (isSessionDead()) return false;
  if (!isRadioRefillActive()) return false;
  if (options.moodShift && !options.momentInterrupt) {
    if (Date.now() - _lastMoodShiftRefillAt < MOOD_SHIFT_DEBOUNCE_MS) return false;
    _lastMoodShiftRefillAt = Date.now();
  }
  return _triggerRefill(options);
}

// ── 30-day history API ───────────────────────────────────────────────

export async function fetchRadioHistory(days = 30): Promise<string[]> {
  try {
    const result: any = await api.get(`/api/maestro-radio/history?days=${days}`);
    return result?.track_ids || [];
  } catch (e) {
    console.warn('[radioRefill] history fetch failed:', e);
    return [];
  }
}

export function recordRadioPlay(
  trackId: string,
  artistName?: string,
  trackTitle?: string,
  stationPrompt?: string,
): void {
  api.post('/api/maestro-radio/history', {
    trackId,
    artistName: artistName || null,
    trackTitle: trackTitle || null,
    stationPrompt: stationPrompt || null,
  }).catch((e: any) => {
    console.warn('[radioRefill] record play failed:', e?.message);
  });
}

// ── Internal ─────────────────────────────────────────────────────────

function _isBuildIngestionActive(): boolean {
  return isEllaRadioBuildPollActive();
}

function parseContinue409(err: unknown): { retryAfterMs: number } | null {
  const msg = String((err as Error)?.message || err || '');
  if (!msg.includes('API 409')) return null;
  try {
    const body = msg.replace(/^API 409:\s*/, '');
    const data = JSON.parse(body) as { retryAfterMs?: number };
    const retryAfterMs = Number(data?.retryAfterMs);
    if (Number.isFinite(retryAfterMs) && retryAfterMs > 0) {
      return { retryAfterMs };
    }
  } catch {
    /* fall through */
  }
  return { retryAfterMs: 5_000 };
}

async function _sleepContinueBackoff(retryAfterMs: number): Promise<void> {
  console.log(`[radioRefill] continue 409 — build owns ingestion, backing off ${retryAfterMs}ms`);
  await new Promise((r) => setTimeout(r, retryAfterMs));
}

function _isEllaRadioSession(): boolean {
  return sessionStorage.getItem('active_ella_radio') === 'true';
}

function _ambientAware(): boolean {
  return ellaRadioSessionRefs.timeAware || ellaRadioSessionRefs.weatherAware;
}

async function _seedAmbientSignature(): Promise<void> {
  if (!_ambientAware()) return;
  try {
    const fresh = await fetchFreshEllaContinueOpts({ activeSession: true });
    seedAmbientReweaveSignature(fresh);
  } catch {
    /* non-fatal */
  }
}

function _serveFromReserve(): boolean {
  if (isRadioArcIngestionPending()) {
    console.log('[radioRefill] reserve serve suppressed — arc ingestion pending');
    return false;
  }
  const batch = pullFromReserve();
  if (batch.length === 0) return false;
  const queueTracks = ellaTracksToQueueFormat(batch);
  QueueController.appendToQueue(queueTracks, 'radioRefill:serveFromReserve');
  _lastRefillAppended = batch.length;
  ellaRadioSessionRefs.onTracksAppended?.(batch);
  console.log('[radioRefill] refill_served_from_reserve', {
    count: batch.length,
    remaining: reserveTankSize(),
  });
  return true;
}

async function _retryContinueInBackground(
  contOpts: Record<string, unknown>,
  playlistId: string,
  attempt = 0,
): Promise<void> {
  if (isSessionDead()) return;
  if (_backgroundRetryInFlight || attempt >= REFILL_MAX_RETRIES) return;
  _backgroundRetryInFlight = true;
  const delay = REFILL_RETRY_BACKOFF_MS[attempt] ?? 20_000;
  await new Promise((r) => setTimeout(r, delay));
  try {
    const res = await smartPlaylistService.continue(playlistId, contOpts as any);
    if (res.poolSlice?.length) setLocalPoolSlice(res.poolSlice);
    if (res.reserveTracks?.length) mergeIntoReserve(res.reserveTracks);
    if (res.tracks?.length) {
      mergeIntoReserve(res.tracks);
      console.log('[radioRefill] refill_retry_recovered', {
        attempt: attempt + 1,
        tracks: res.tracks.length,
        reserve: reserveTankSize(),
      });
      _backgroundRetryInFlight = false;
      return;
    }
  } catch (e: any) {
    const conflict = parseContinue409(e);
    if (conflict) {
      await _sleepContinueBackoff(conflict.retryAfterMs);
      _backgroundRetryInFlight = false;
      return;
    }
    console.warn(`[radioRefill] refill retry ${attempt + 1}/${REFILL_MAX_RETRIES} failed:`, e?.message || e);
  }
  _backgroundRetryInFlight = false;
  if (attempt + 1 < REFILL_MAX_RETRIES) {
    void _retryContinueInBackground(contOpts, playlistId, attempt + 1);
    return;
  }
  if (reserveTankSize() === 0) {
    const line = "I'm having trouble reaching the music library right now — let's pause here and pick back up in a moment.";
    ellaRadioSessionRefs.onSessionEndGraceful?.(line);
  }
}

function _endSessionGracefully(reason: string): void {
  const line = reason || "I've run out of tracks for now — tap play when you're ready to start fresh.";
  ellaRadioSessionRefs.onSessionEndGraceful?.(line);
}

function _shouldRefill(state: ReturnType<typeof useMaestroStore.getState>): boolean {
  if (!_refillWatcherArmed) return false;
  if (_isBuildIngestionActive()) return false;
  if (isRadioArcIngestionPending()) return false;
  if (!isRadioRefillActive() && !_isEllaRadioSession()) return false;
  if (state.queue.length === 0 || state.queueIndex < 0) return false;

  const remaining = state.queue.length - state.queueIndex - 1;
  const atLastTrack = state.queueIndex >= state.queue.length - 1;
  const playbackInactive = state.playbackState === 'stopped' || state.playbackState === 'paused';

  return remaining <= REFILL_THRESHOLD || (atLastTrack && remaining <= 0 && playbackInactive);
}

function _stateSnapshot(state: ReturnType<typeof useMaestroStore.getState>): string {
  return `${state.queueIndex}:${state.queue.length}:${state.playbackState}`;
}

function _armRefillWatcher(): void {
  _lastSnapshot = '';
  _startWatching();
}

function _startWatching(): void {
  if (_unsub) return;

  _unsub = useMaestroStore.subscribe((state) => {
    if (isSessionDead()) return;
    if (!isRadioRefillActive() && !_isEllaRadioSession()) return;
    if (state.activeSource !== 'radio' && !_isEllaRadioSession()) return;

    const snapshot = _stateSnapshot(state);
    if (snapshot !== _lastSnapshot) {
      _lastSnapshot = snapshot;

      if (_shouldRefill(state) && !_refilling) {
        void _triggerRefill();
      }
    }
  });
}

async function _triggerRefill(options: RefillRequestOptions = {}): Promise<boolean> {
  if (isSessionDead()) return false;
  if (_isBuildIngestionActive()) {
    console.log('[radioRefill] refill suppressed — build poll owns ingestion');
    return false;
  }
  if (isRadioArcIngestionPending()) {
    console.log('[radioRefill] refill suppressed — arc tracks pending reconcile enqueue');
    return false;
  }
  if (!_refillWatcherArmed && !options.urgent) {
    return false;
  }
  if (_refilling) {
    if (options.momentInterrupt) _pendingMoment = true;
    return false;
  }
  _refilling = true;
  if (!options.momentInterrupt) _lastRefillAppended = 0;

  const playlistId = ellaRadioSessionRefs.ellaPlaylistId;
  if (!playlistId) {
    _refilling = false;
    return false;
  }

  const beforeQueueLen = useMaestroStore.getState().queue.length;
  const beforeIndex = useMaestroStore.getState().queueIndex;
  const wasAtEnd = beforeIndex >= beforeQueueLen - 1;

  let contOpts: Record<string, unknown> = {};

  try {
    const historyIds = await fetchRadioHistory(30);
    const sessionPlayed = ellaRadioSessionRefs.getPlayedTrackIds();
    const allExcluded = [...new Set([...historyIds, ...sessionPlayed])];

    const freshAmbient = options.freshAmbient ?? await fetchFreshEllaContinueOpts({ activeSession: true });
    const ambientSliders = freshAmbient.ambientEnergy != null
      ? {
          energy: freshAmbient.ambientEnergy as number,
          acoustic: freshAmbient.ambientAcoustic as number,
          valence: freshAmbient.ambientValence as number,
          vocal: freshAmbient.ambientVocal as number,
        }
      : null;

    const baseTuning = ellaRadioSessionRefs.buildTuningParams();
    const tuningParams = applyAmbientSlidersToTuning(baseTuning, ambientSliders);

    contOpts = {
      playedTrackIds: allExcluded,
      skippedTrackIds: ellaRadioSessionRefs.getSkippedTrackIds(),
      tuningParams,
      tweaks: ellaRadioSessionRefs.getTweaks(),
      ...freshAmbient,
    };

    if (options.momentInterrupt) {
      contOpts.momentInterrupt = true;
      contOpts.momentReason = options.momentReason;
      contOpts.previousAmbientLabel = options.previousAmbientLabel;
      contOpts.newAmbientLabel = options.newAmbientLabel;
    }

    const res = await smartPlaylistService.continue(playlistId, contOpts as any);
    if (res.poolSlice?.length) setLocalPoolSlice(res.poolSlice);
    if (res.reserveTracks?.length) bankReserveTracks(res.reserveTracks);

    if (res.tracks?.length > 0) {
      const queueTracks = ellaTracksToQueueFormat(res.tracks);

      if (options.momentInterrupt) {
        QueueController.insertPlayNext(queueTracks, 'radioRefill:momentInterrupt');
        if (res.ellaResponse) {
          ellaRadioSessionRefs.onMomentInterrupt?.(res.ellaResponse, res.tracks);
        }
        console.log(`[radioRefill] moment interrupt — ${res.tracks.length} tracks queued as play-next`);
      } else {
        QueueController.appendToQueue(queueTracks, 'radioRefill:continue');
        _lastRefillAppended = res.tracks.length;
      }

      ellaRadioSessionRefs.onTracksAppended?.(res.tracks);

      const after = useMaestroStore.getState();
      if (!options.momentInterrupt) {
        console.log(
          `[radioRefill] appended ${res.tracks.length} tracks (reserve bank ${reserveTankSize()}). Queue remaining: ${after.queue.length - after.queueIndex - 1}`,
        );
      }

      if (wasAtEnd && after.queueIndex + 1 < after.queue.length) {
        const playback = after.playbackState;
        if (playback === 'stopped' || playback === 'paused' || options.urgent) {
          const nextIdx = after.queueIndex + 1;
          if (nextIdx < after.queue.length) {
            console.log('[radioRefill] resuming playback after end-of-queue refill');
            void QueueController.playQueueTrack(nextIdx);
          }
        }
      }

      return true;
    }
  } catch (e: any) {
    const conflict = parseContinue409(e);
    if (conflict) {
      await _sleepContinueBackoff(conflict.retryAfterMs);
      return false;
    }
    console.error('[radioRefill] refill failed:', e?.message || e);
    if (_serveFromReserve()) {
      void _retryContinueInBackground(contOpts, playlistId);
      const afterReserve = useMaestroStore.getState();
      if (wasAtEnd && afterReserve.queueIndex + 1 < afterReserve.queue.length && options.urgent) {
        void QueueController.playQueueTrack(afterReserve.queueIndex + 1);
      }
      return true;
    }
    void _retryContinueInBackground(contOpts, playlistId);
    if (options.urgent && reserveTankSize() === 0) {
      _endSessionGracefully("I'm having a little trouble keeping the music flowing — let's take a breath and try again soon.");
    }
  } finally {
    _refilling = false;
    if (_pendingMoment) {
      _pendingMoment = false;
    }
  }

  return false;
}

/** Blocking wait used by QueueController when playlist ends during Ella Radio. */
export async function waitForEllaRadioRefill(timeoutMs = URGENT_REFILL_TIMEOUT_MS): Promise<boolean> {
  if (!isRadioRefillActive()) return false;

  const initial = useMaestroStore.getState();
  if (initial.queueIndex + 1 < initial.queue.length) return true;

  if (_serveFromReserve()) return true;

  const refillPromise = requestEllaRadioRefill({ urgent: true });
  const started = Date.now();

  while (Date.now() - started < timeoutMs) {
    if (_lastRefillAppended > 0) return true;
    if (_serveFromReserve()) return true;
    const state = useMaestroStore.getState();
    if (state.queueIndex + 1 < state.queue.length) return true;
    await new Promise((r) => setTimeout(r, 400));
  }

  if (_serveFromReserve()) return true;

  await refillPromise.catch(() => {});
  const final = useMaestroStore.getState();
  return _lastRefillAppended > 0 || final.queueIndex + 1 < final.queue.length || _serveFromReserve();
}
